import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Check, ArrowRight, ChevronRight, ChevronLeft, Building2, User, Landmark, FileCheck, Download, Search } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { jsPDF } from 'jspdf';
import { trackPageView, trackFormSubmit } from '../utils/analytics';
import SEOHead from '../components/SEOHead';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const steps = [
  { label: 'Company Info', icon: Building2 },
  { label: 'Personal Details', icon: User },
  { label: 'Bank Details', icon: Landmark },
  { label: 'Documents & Declaration', icon: FileCheck },
];

const entityTypes = ['Proprietor', 'Partner', 'Director', 'Karta'];

const requiredDocs = [
  'Copy of Partnership Deed / Memorandum articles of association',
  'Copy of GST Registration',
  'Copy of PAN Card of all partners',
  'Copy of Photo ID Proof',
  'Letter Head of the firm',
];

const DealerFormPage = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    company_name: '',
    year_of_establishment: '',
    pan_number: '',
    gst_number: '',
    company_email: '',
    entity_type: '',
    key_contact_person: '',
    name: '',
    father_husband_name: '',
    residence_address: '',
    taluka: '',
    district: '',
    state: '',
    phone: '',
    mobile: '',
    email: '',
    bank_name: '',
    account_type: 'Saving',
    account_number: '',
    signatory_name: '',
    documents_checklist: [],
    declaration_agreed: false,
  });

  useEffect(() => { trackPageView('/dealer-form', 'Digital Dealer Form'); }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleDocToggle = (doc) => {
    setFormData(prev => ({
      ...prev,
      documents_checklist: prev.documents_checklist.includes(doc)
        ? prev.documents_checklist.filter(d => d !== doc)
        : [...prev.documents_checklist, doc]
    }));
  };

  const validateStep = (step) => {
    switch (step) {
      case 1: return formData.company_name && formData.pan_number && formData.gst_number && formData.company_email && formData.entity_type;
      case 2: return formData.name && formData.mobile && formData.email && formData.district && formData.state;
      case 3: return formData.bank_name && formData.account_number;
      case 4: return formData.declaration_agreed;
      default: return true;
    }
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 4));
    } else {
      toast.error('Please fill in all required fields');
    }
  };

  const handlePrev = () => setCurrentStep(prev => Math.max(prev - 1, 1));

  const handleSubmit = async () => {
    if (!formData.declaration_agreed) {
      toast.error('Please agree to the declaration to proceed');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/dealer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        toast.success('Dealer application submitted successfully! We will contact you soon.');
        trackFormSubmit('dealer_application', { entity_type: formData.entity_type });
        setFormData({
          company_name: '', year_of_establishment: '', pan_number: '', gst_number: '',
          company_email: '', entity_type: '', key_contact_person: '', name: '',
          father_husband_name: '', residence_address: '', taluka: '', district: '',
          state: '', phone: '', mobile: '', email: '', bank_name: '',
          account_type: 'Saving', account_number: '', signatory_name: '',
          documents_checklist: [], declaration_agreed: false,
        });
        setCurrentStep(1);
      } else {
        throw new Error('Submission failed');
      }
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputClass = "w-full bg-void border border-white/10 focus:border-coral rounded-xl px-4 py-3.5 text-white placeholder:text-gray-600 outline-none text-sm";

  const downloadPDF = () => {
    const doc = new jsPDF();
    const m = 20;
    let y = m;
    const pw = doc.internal.pageSize.getWidth();

    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('AVI CEMENT LIMITED', pw / 2, y, { align: 'center' });
    y += 8;
    doc.setFontSize(12);
    doc.text('Dealer Application Form', pw / 2, y, { align: 'center' });
    y += 12;
    doc.setDrawColor(232, 90, 28);
    doc.line(m, y, pw - m, y);
    y += 10;

    const addSection = (title, fields) => {
      if (y > 260) { doc.addPage(); y = m; }
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(232, 90, 28);
      doc.text(title, m, y);
      y += 7;
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(10);
      fields.forEach(([label, value]) => {
        if (y > 275) { doc.addPage(); y = m; }
        doc.text(`${label}:`, m, y);
        doc.text(String(value || 'N/A'), m + 60, y);
        y += 6;
      });
      y += 5;
    };

    addSection('Company Information', [
      ['Company Name', formData.company_name],
      ['Year of Est.', formData.year_of_establishment],
      ['PAN Number', formData.pan_number],
      ['GST Number', formData.gst_number],
      ['Email', formData.company_email],
      ['Entity Type', formData.entity_type],
      ['Contact Person', formData.key_contact_person],
    ]);

    addSection('Personal & Contact Details', [
      ['Full Name', formData.name],
      ['Father/Husband', formData.father_husband_name],
      ['Address', formData.residence_address],
      ['Taluka', formData.taluka],
      ['District', formData.district],
      ['State', formData.state],
      ['Phone', formData.phone],
      ['Mobile', formData.mobile],
      ['Email', formData.email],
    ]);

    addSection('Bank Details', [
      ['Bank Name', formData.bank_name],
      ['Account Type', formData.account_type],
      ['Account No.', formData.account_number],
      ['Signatory', formData.signatory_name],
    ]);

    if (formData.documents_checklist.length > 0) {
      addSection('Documents Checklist', formData.documents_checklist.map((d, i) => [`Doc ${i + 1}`, d]));
    }

    doc.setFontSize(8);
    doc.setTextColor(128, 128, 128);
    doc.text(`Generated on ${new Date().toLocaleDateString()} | Avi Cement Limited`, pw / 2, 290, { align: 'center' });

    doc.save(`Dealer_Application_${formData.company_name || 'Form'}.pdf`);
    toast.success('PDF downloaded successfully!');
  };

  return (
    <div className="min-h-screen bg-void" data-testid="dealer-form-page">
      <SEOHead
        title="Dealer Application Form"
        description="Apply to become an authorized Avi Cement dealer. Submit your company details, PAN, GST, and bank information. Review within 48 hours."
        path="/dealer-form"
      />
      {/* Hero */}
      <section className="relative py-12 md:py-28 overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-20" />
        <motion.div
          className="absolute top-1/3 left-0 w-[600px] h-[600px] rounded-full"
          style={{ background: 'radial-gradient(circle, rgba(232,90,28,0.08) 0%, transparent 60%)' }}
        />
        <div className="container mx-auto px-4 md:px-8 max-w-7xl relative z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-3xl">
            <span className="text-coral font-mono text-sm mb-4 block">BUSINESS ASSOCIATES</span>
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              Dealer<br /><span className="text-gradient">Application</span>
            </h1>
            <p className="text-gray-400 text-lg">Apply to become an authorized Avi Cement dealer. Fill out the application form below and our team will review your application.</p>
            <Link to="/dealer-status" className="inline-flex items-center gap-2 mt-4 text-[#F5A623] hover:text-[#E85A1C] text-sm font-medium transition-colors" data-testid="dealer-form-status-link">
              <Search className="w-4 h-4" /> Already applied? Check your status
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Form */}
      <section className="pb-20 md:pb-32">
        <div className="container mx-auto px-4 md:px-8 max-w-3xl">
          {/* Progress Steps */}
          <div className="flex items-center justify-between mb-12">
            {steps.map((step, index) => (
              <div key={step.label} className="flex items-center">
                <div className="flex flex-col items-center">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center font-display text-sm transition-colors ${
                    currentStep > index + 1 ? 'bg-mint text-void'
                      : currentStep === index + 1 ? 'bg-coral text-white'
                      : 'bg-surface-light text-gray-500'
                  }`}>
                    {currentStep > index + 1 ? <Check className="w-5 h-5" /> : <step.icon className="w-4 h-4" />}
                  </div>
                  <span className={`hidden sm:block mt-2 text-xs ${currentStep >= index + 1 ? 'text-white' : 'text-gray-500'}`}>
                    {step.label}
                  </span>
                </div>
                {index < 3 && <div className={`w-8 sm:w-16 h-px mx-2 mt-[-16px] sm:mt-[-24px] ${currentStep > index + 1 ? 'bg-mint' : 'bg-surface-light'}`} />}
              </div>
            ))}
          </div>

          <div className="bg-surface border border-white/5 rounded-2xl p-6 md:p-8">
            {/* Step 1: Company Info */}
            {currentStep === 1 && (
              <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-6">
                <h3 className="font-display text-xl text-white mb-2">Company Information</h3>
                <p className="text-gray-500 text-sm mb-6">Please fill in your company details as registered.</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <input name="company_name" placeholder="Company / Firm Name *" value={formData.company_name} onChange={handleChange} className={inputClass} data-testid="dealer-company-name" />
                  <input name="year_of_establishment" placeholder="Year of Establishment" value={formData.year_of_establishment} onChange={handleChange} className={inputClass} data-testid="dealer-year" />
                  <input name="pan_number" placeholder="PAN Number *" value={formData.pan_number} onChange={handleChange} className={inputClass} data-testid="dealer-pan" />
                  <input name="gst_number" placeholder="GST Number *" value={formData.gst_number} onChange={handleChange} className={inputClass} data-testid="dealer-gst" />
                  <input name="company_email" type="email" placeholder="Company Email *" value={formData.company_email} onChange={handleChange} className={inputClass} data-testid="dealer-company-email" />
                  <input name="key_contact_person" placeholder="Key Contact Person" value={formData.key_contact_person} onChange={handleChange} className={inputClass} data-testid="dealer-contact-person" />
                </div>
                <div>
                  <label className="text-gray-400 text-sm mb-3 block">Entity Type *</label>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {entityTypes.map((type) => (
                      <button
                        key={type}
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, entity_type: type }))}
                        data-testid={`dealer-entity-${type.toLowerCase()}`}
                        className={`p-3 rounded-xl text-sm font-display transition-all ${
                          formData.entity_type === type
                            ? 'bg-coral text-white'
                            : 'bg-void border border-white/10 text-gray-400 hover:border-white/30'
                        }`}
                      >
                        {type}
                      </button>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}

            {/* Step 2: Personal Details */}
            {currentStep === 2 && (
              <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-6">
                <h3 className="font-display text-xl text-white mb-2">Personal & Contact Details</h3>
                <p className="text-gray-500 text-sm mb-6">Provide your personal identification and contact details.</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <input name="name" placeholder="Full Name *" value={formData.name} onChange={handleChange} className={inputClass} data-testid="dealer-name" />
                  <input name="father_husband_name" placeholder="Father / Husband Name" value={formData.father_husband_name} onChange={handleChange} className={inputClass} data-testid="dealer-father-name" />
                </div>
                <textarea name="residence_address" placeholder="Residence Address" value={formData.residence_address} onChange={handleChange} rows={3} className={`${inputClass} resize-none`} data-testid="dealer-address" />
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                  <input name="taluka" placeholder="Taluka" value={formData.taluka} onChange={handleChange} className={inputClass} data-testid="dealer-taluka" />
                  <input name="district" placeholder="District *" value={formData.district} onChange={handleChange} className={inputClass} data-testid="dealer-district" />
                  <input name="state" placeholder="State *" value={formData.state} onChange={handleChange} className={inputClass} data-testid="dealer-state" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                  <input name="phone" type="tel" placeholder="Telephone No." value={formData.phone} onChange={handleChange} className={inputClass} data-testid="dealer-phone" />
                  <input name="mobile" type="tel" placeholder="Mobile No. *" value={formData.mobile} onChange={handleChange} className={inputClass} data-testid="dealer-mobile" />
                  <input name="email" type="email" placeholder="Email *" value={formData.email} onChange={handleChange} className={inputClass} data-testid="dealer-email" />
                </div>
              </motion.div>
            )}

            {/* Step 3: Bank Details */}
            {currentStep === 3 && (
              <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-6">
                <h3 className="font-display text-xl text-white mb-2">Bank Details</h3>
                <p className="text-gray-500 text-sm mb-6">Provide your bank account information for financial processing.</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <input name="bank_name" placeholder="Bank Name *" value={formData.bank_name} onChange={handleChange} className={inputClass} data-testid="dealer-bank-name" />
                  <div>
                    <label className="text-gray-500 text-xs mb-1 block">Account Type</label>
                    <div className="flex gap-3">
                      {['Saving', 'Current', 'Other'].map((type) => (
                        <button
                          key={type}
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, account_type: type }))}
                          data-testid={`dealer-account-${type.toLowerCase()}`}
                          className={`flex-1 p-3 rounded-xl text-sm font-display transition-all ${
                            formData.account_type === type
                              ? 'bg-coral text-white'
                              : 'bg-void border border-white/10 text-gray-400 hover:border-white/30'
                          }`}
                        >
                          {type}
                        </button>
                      ))}
                    </div>
                  </div>
                  <input name="account_number" placeholder="Account Number *" value={formData.account_number} onChange={handleChange} className={inputClass} data-testid="dealer-account-number" />
                  <input name="signatory_name" placeholder="Authorised Signatory Name" value={formData.signatory_name} onChange={handleChange} className={inputClass} data-testid="dealer-signatory" />
                </div>
              </motion.div>
            )}

            {/* Step 4: Documents & Declaration */}
            {currentStep === 4 && (
              <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-6">
                <h3 className="font-display text-xl text-white mb-2">Documents & Declaration</h3>
                <p className="text-gray-500 text-sm mb-6">Confirm the documents you will submit and agree to the declaration.</p>

                <div>
                  <label className="text-gray-400 text-sm mb-3 block">Required Documents Checklist</label>
                  <div className="space-y-3">
                    {requiredDocs.map((doc, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => handleDocToggle(doc)}
                        data-testid={`dealer-doc-${i}`}
                        className={`w-full flex items-center gap-3 p-4 rounded-xl text-left transition-all ${
                          formData.documents_checklist.includes(doc)
                            ? 'bg-coral/10 border border-coral/30 text-white'
                            : 'bg-void border border-white/10 text-gray-400 hover:border-white/20'
                        }`}
                      >
                        <div className={`w-5 h-5 rounded border flex items-center justify-center flex-shrink-0 ${
                          formData.documents_checklist.includes(doc) ? 'border-coral bg-coral' : 'border-gray-600'
                        }`}>
                          {formData.documents_checklist.includes(doc) && <Check className="w-3 h-3 text-white" />}
                        </div>
                        <span className="text-sm">{i + 1}. {doc}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="border-t border-white/5 pt-6">
                  <h4 className="font-display text-lg text-white mb-4">Declaration</h4>
                  <div className="bg-void/50 border border-white/5 rounded-xl p-5 mb-4">
                    <p className="text-gray-400 text-sm leading-relaxed">
                      I/We do hereby declare that the information furnished herein is correct to the best of my/our knowledge and belief. For any incorrect information/misinformation furnished and for non-compliance of the company's policies formulated from time to time, I/We agree that:
                    </p>
                    <ol className="list-decimal list-inside text-gray-500 text-sm mt-3 space-y-2">
                      <li>The company shall have the absolute right to reject my/our application for appointment as Business Associates/Sales Promoter.</li>
                      <li>The company reserves the right to terminate my/our dealership without any notice and assigning any reason.</li>
                      <li>The company shall have the right to forfeit or adjust the whole or part of my/our security deposit with them in the manner they may deem fit.</li>
                    </ol>
                  </div>
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, declaration_agreed: !prev.declaration_agreed }))}
                    data-testid="dealer-declaration-agree"
                    className={`flex items-center gap-3 p-4 rounded-xl w-full text-left transition-all ${
                      formData.declaration_agreed
                        ? 'bg-mint/10 border border-mint/30 text-white'
                        : 'bg-void border border-white/10 text-gray-400 hover:border-white/20'
                    }`}
                  >
                    <div className={`w-6 h-6 rounded border flex items-center justify-center flex-shrink-0 ${
                      formData.declaration_agreed ? 'border-mint bg-mint' : 'border-gray-600'
                    }`}>
                      {formData.declaration_agreed && <Check className="w-4 h-4 text-void" />}
                    </div>
                    <span className="text-sm font-display font-medium">I agree to the above declaration</span>
                  </button>
                </div>
              </motion.div>
            )}

            {/* Navigation */}
            <div className="flex justify-between items-center mt-8">
              {currentStep > 1 ? (
                <button onClick={handlePrev} className="flex items-center gap-2 text-gray-400 hover:text-white font-display transition-colors" data-testid="dealer-prev-btn">
                  <ChevronLeft className="w-5 h-5" /> Previous
                </button>
              ) : <div />}
              <div className="flex items-center gap-3">
                <button onClick={downloadPDF} data-testid="dealer-download-pdf" className="flex items-center gap-2 border border-white/10 hover:border-white/30 text-gray-300 hover:text-white font-display px-5 py-3 rounded-full transition-all text-sm">
                  <Download className="w-4 h-4" /> Download PDF
                </button>
                {currentStep < 4 ? (
                  <button onClick={handleNext} data-testid="dealer-next-btn" className="flex items-center gap-2 bg-coral hover:bg-coral-dark text-white font-display px-6 py-3 rounded-full transition-colors">
                    Next <ChevronRight className="w-5 h-5" />
                  </button>
                ) : (
                  <button onClick={handleSubmit} disabled={isSubmitting} data-testid="dealer-submit-btn" className="flex items-center gap-2 bg-coral hover:bg-coral-dark text-white font-display px-6 py-3 rounded-full transition-colors disabled:opacity-50">
                    {isSubmitting ? 'Submitting...' : 'Submit Application'} <ArrowRight className="w-5 h-5" />
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DealerFormPage;
